import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import api, { formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { Heart, Sparkles, Loader2 } from "lucide-react";

export default function JoinByLink() {
  const { token } = useParams();
  const { user, couple, refresh, login, register } = useAuth();
  const navigate = useNavigate();

  const [info, setInfo] = useState(null); // {inviter, couple_id, valid}
  const [error, setError] = useState(null);
  const [tab, setTab] = useState("login"); // login|register
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const [joining, setJoining] = useState(false);

  // Validate token on mount
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/couple/invite-info?token=${encodeURIComponent(token)}`);
        if (!cancelled) setInfo(data);
      } catch (err) {
        if (!cancelled) setError(formatApiErrorDetail(err.response?.data?.detail) || "Einladung ungültig");
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  // If already authenticated AND not in a couple, accept the invite immediately
  useEffect(() => {
    if (error) return;
    if (!info || !user || user === false || joining) return;
    if (couple) return; // already paired — handled below
    (async () => {
      setJoining(true);
      try {
        await api.post("/couple/join-by-token", { token });
        await refresh();
        toast.success("Couple Space verbunden 💛");
        navigate("/dashboard");
      } catch (err) {
        setError(formatApiErrorDetail(err.response?.data?.detail) || "Beitritt fehlgeschlagen");
      } finally {
        setJoining(false);
      }
    })();
  }, [info, user, couple, token, refresh, navigate, joining]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (tab === "register") {
        await register(email, password, name);
      } else {
        await login(email, password);
      }
      // After auth the join useEffect above will fire automatically.
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      setLoading(false);
    }
  };

  // Already in a different couple
  if (user && user !== false && couple) {
    return (
      <div className="min-h-screen bg-alabaster grain flex justify-center p-6 py-10">
        <div className="max-w-md w-full my-auto bg-white border border-border rounded-3xl p-8 sm:p-12 text-center">
          <div className="w-14 h-14 rounded-full bg-bone mx-auto flex items-center justify-center mb-6">
            <Heart size={22} className="text-terracotta" />
          </div>
          <h1 className="font-serif text-2xl text-ink mb-3">Du bist schon verbunden</h1>
          <p className="text-text_secondary mb-8">
            Du bist bereits Teil eines Couple Space. Verlasse ihn zuerst in den Einstellungen, falls du diesem Link folgen möchtest.
          </p>
          <Button onClick={() => navigate("/dashboard")} className="rounded-full bg-ink text-alabaster hover:bg-ink/90 px-6" data-testid="join-already-paired-back">
            Zurück zum Dashboard
          </Button>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-alabaster grain flex justify-center p-6 py-10">
        <div className="max-w-md w-full my-auto bg-white border border-border rounded-3xl p-8 sm:p-12 text-center" data-testid="join-error-card">
          <h1 className="font-serif text-2xl text-ink mb-3">Einladung nicht gültig</h1>
          <p className="text-text_secondary mb-8">{error}</p>
          <Link to="/login">
            <Button className="rounded-full bg-ink text-alabaster hover:bg-ink/90 px-6">Zur Anmeldung</Button>
          </Link>
        </div>
      </div>
    );
  }

  if (!info) {
    return (
      <div className="min-h-screen bg-alabaster grain flex items-center justify-center p-6">
        <Loader2 className="animate-spin text-text_secondary" size={28} />
      </div>
    );
  }

  // Logged in but joining is happening
  if (user && user !== false && joining) {
    return (
      <div className="min-h-screen bg-alabaster grain flex items-center justify-center p-6">
        <div className="text-center">
          <Loader2 className="animate-spin text-terracotta mx-auto mb-4" size={28} />
          <p className="text-text_secondary">Verbinde euren Couple Space …</p>
        </div>
      </div>
    );
  }

  // Not logged in — show the inviter card + auth form
  const inviterName = info.inviter?.name || "Jemand";
  const initial = inviterName.charAt(0).toUpperCase();

  return (
    <div className="min-h-screen bg-alabaster grain flex justify-center p-6 py-10">
      <div className="max-w-md w-full my-auto" data-testid="join-by-link-page">
        <Link to="/" className="flex items-center justify-center gap-3 mb-8">
          <div className="w-10 h-10 rounded-full bg-ink text-alabaster flex items-center justify-center">
            <Sparkles size={18} strokeWidth={1.6} />
          </div>
          <div className="font-serif text-2xl text-ink">Wisha</div>
        </Link>

        <div className="bg-white border border-border rounded-3xl p-8 sm:p-10 text-center mb-6">
          <div className="w-16 h-16 rounded-full bg-terracotta text-white mx-auto flex items-center justify-center text-2xl font-medium mb-5" data-testid="join-inviter-avatar">
            {initial}
          </div>
          <h1 className="font-serif text-3xl text-ink leading-tight mb-2">
            <span className="text-terracotta italic">{inviterName}</span> möchte<br />dich verbinden.
          </h1>
          <p className="text-text_secondary text-sm mt-3">
            Tretet eurem gemeinsamen Wisha-Space bei und plant Wünsche, Reisen und Date-Ideen zusammen.
          </p>
        </div>

        <div className="bg-white border border-border rounded-3xl p-6 sm:p-8">
          <div className="flex gap-1 p-1 bg-bone rounded-full mb-6">
            <button
              onClick={() => setTab("login")}
              className={`flex-1 py-2 rounded-full text-sm transition ${tab === "login" ? "bg-white text-ink shadow-sm" : "text-text_secondary"}`}
              data-testid="join-tab-login"
            >
              Einloggen
            </button>
            <button
              onClick={() => setTab("register")}
              className={`flex-1 py-2 rounded-full text-sm transition ${tab === "register" ? "bg-white text-ink shadow-sm" : "text-text_secondary"}`}
              data-testid="join-tab-register"
            >
              Registrieren
            </button>
          </div>

          <form onSubmit={submit} className="space-y-4">
            {tab === "register" && (
              <div>
                <label className="text-[10px] tracking-[0.25em] uppercase text-text_secondary">Name</label>
                <Input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  className="mt-1 rounded-2xl border-border bg-bone py-6"
                  placeholder="Wie heißt du?"
                  data-testid="join-name-input"
                />
              </div>
            )}
            <div>
              <label className="text-[10px] tracking-[0.25em] uppercase text-text_secondary">E-Mail</label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="mt-1 rounded-2xl border-border bg-bone py-6"
                placeholder="dein@email.de"
                data-testid="join-email-input"
              />
            </div>
            <div>
              <label className="text-[10px] tracking-[0.25em] uppercase text-text_secondary">Passwort</label>
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={8}
                className="mt-1 rounded-2xl border-border bg-bone py-6"
                placeholder="••••••••"
                data-testid="join-password-input"
              />
            </div>
            <Button
              type="submit"
              disabled={loading}
              className="w-full rounded-full bg-ink text-alabaster hover:bg-ink/90 py-6"
              data-testid="join-submit"
            >
              {loading ? "…" : tab === "register" ? "Registrieren & verbinden" : "Einloggen & verbinden"}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
